import PropTypes from "prop-types";
import { withDebug } from "../../util/debug";
import React from "react";

// Lazy load components dynamically
const LazyImport = (importFn) => React.lazy(importFn);

// null component to catch stuff we haven't implemented yet
const UnimplementedComponent = ({ entity: { render_name } }) => (
  <p>I think this is a {render_name} but it isn&apos;t implemented yet.</p>
);

UnimplementedComponent.propTypes = {
  entity: PropTypes.object,
};

export default {
  UNIMPLEMENTED: {
    component: withDebug(UnimplementedComponent),
    extract: [],
  },
  // define properties we should always look for
  EXTRACT_ALWAYS: [
    "align_image",
    "banner_image",
    "image",
    "image_url",
    "alt_text",
    "banner_title",
    "title",
    "sub_title",
    "banner_subtitle",
    "kicker",
    "header",
    "basic_html",
    "call_to_action",
    "button_url",
    "button_text",
    "button_theme",
    "text",
    "copy"
  ],
  article_header: {
    component: LazyImport(() => import("@/blades/ArticleHeaderBlade")),
    extract: ["header_type"],
  },
  blade_large_hero: {
    // Greetings future Hero Blade devs, go ahead and use your component for the correct one here,
    // the short hero blade is just in use here until its ready.
    component: LazyImport(() => import("@/blades/HeroBlade")),
    extract: ["text_theme", "image_size", "title_tag"],
  },
  blade_flex_hero: {
    // Greetings future Hero Blade devs, go ahead and use your component for the correct one here,
    // the short hero blade is just in use here until its ready.
    component: LazyImport(() => import("@/blades/FlexHeroBlade")),
    extract: ["image_size", "text_theme", "align_copy_horizontal", "align_copy_vertical", "hero_image", "buttons", "button"],
  },
  button: {
    component: LazyImport(() => import("@/pages/blog/BlogButton")),
    extract: []
  },
  faq: {
    component: LazyImport(() => import("@/blades/FaqBlade")),
    extract: ["faq"],
  },
  flag_pane: {
    component: LazyImport(() => import("@/blades/FlagBlade")),
    extract: ["flag_title", "flag_subtitle"],
  },
  flag_pane_fixed_ratio: {
    component: LazyImport(() => import("@/blades/FlagBlade")),
    extract: ["flag_title", "flag_subtitle"],
  },
  article_blurb: {
    component: LazyImport(() => import("@/blades/ArticleBlurbBlade")),
    extract: ["blurb_title", "blurb_description"],
  },
  blade_lp_hero: {
    component: withDebug(UnimplementedComponent),
    extract: [],
  },
  blade_highlight_buttons: {
    component: LazyImport(() => import("@/blades/HighlightButtonsBlade")),
    extract: [
      "headline",
      "button_one",
      "button_two",
      "button_three",
      "button_four",
    ],
  },
  copy: {
    component: LazyImport(() => import("@/blades/CopyBlade")),
    extract: ["copy_text"],
  },
  copy_wrapping_items: {
    component: LazyImport(() => import("@/blades/CopyWrappingItems")),
    extract: [
      "copy",
      "desktop_wrap_count",
      "copy_item"
    ],
  },
  basic_html: {
    component: LazyImport(() => import("@/blades/HtmlBlade")),
    extract: [],
  },
  code_snippet: {
    component: LazyImport(() => import("@/blades/CodeSnippet")),
    extract: ["code_snippet"],
  },
  form_wrapper: {
    component: LazyImport(() => import("@/blades/FormWrapperBlade")),
    extract: [
      "title",
      "sub_title",
      "form_url",
      "form_type_id",
      "submit_cta",
      "options",
    ],
  },
  blade_three_column: {
    component: LazyImport(() => import("@/blades/MultiColumnBlade")),
    extract: [
      "column_one",
      "column_two",
      "column_three",
      "card",
      "flag_pane_card",
      "flag_pane_header_card",
      "flag_pane_video_modal_card",
      "flag_pane_form_modal_card",
      "image",
      "icon",
      "caption",
      "video",
      "video_provider",
      "video_id",
      "flex_card",
      "text_theme",
      "title",
      "text",
      "call_to_action"
    ],
  },
  blade_two_column: {
    component: LazyImport(() => import("@/blades/MultiColumnBlade")),
    extract: [
      "column_one",
      "column_two",
      "card",
      "flag_pane_card",
      "flag_pane_header_card",
      "flag_pane_video_modal_card",
      "flag_pane_form_modal_card",
      "image",
      "icon",
      "caption",
      "video",
      "video_provider",
      "video_id",
      "flex_card",
      "text_theme",
      "title",
      "text",
      "call_to_action"
    ],
  },
  blade_two_column_focus: {
    component: LazyImport(() => import("@/blades/TwoColFocusBlade")),
    extract: [
        "split_ratio",
        "focus_side",
        "column_one",
        "column_two"
    ]
  },
  columned_media: {
    component: LazyImport(() => import("@/blades/ColumnedMedia")),
    extract: [
        "entity",
      "image_size",
      "text_theme",
      "video",
      "video_provider",
      "video_id",
    ]
  },
  size_stepped_image: {
    component: LazyImport(() => import("@/blades/SizeSteppedImageBlade")),
    extract: [
      "desktop_lg_image",
      "desktop_image",
      "tablet_image",
      "mobile_image",
      "accessibility_caption",
    ]
  },
  multi_column_image_copy: {
    component: LazyImport(() => import("@/blades/MultiColumnImageCopyBlade")),
    extract: [
      "text_important",
      "size_stepped_image_component",
      "desktop_lg_image",
      "desktop_image",
      "tablet_image",
      "mobile_image",
      "accessibility_caption",
      "copy_col_count",
      "copy_array",
    ]
  },
  multi_carousel: {
    component: LazyImport(() => import("@/blades/MultiCarouselBlade")),
    extract: [
      "entity",
      "multi_carousel_parent_group",
      "title",
      "video",
      "video_provider",
      "video_id",
    ]
  },
  vertical_spacer: {
    component: LazyImport(() => import("@/blades/VerticalSpacerBlade")),
    extract: [
        'vertical_spacer'
    ]
  },
  blade_feature_layered: {
    component: withDebug(UnimplementedComponent),
    extract: ["featured_image", "product_info"],
  },
  blade_feature_layered_two: {
    component: withDebug(UnimplementedComponent),
    extract: [],
  },
  document_group: {
    component: LazyImport(() => import("@/blades/DocumentGroupBlade")),
    extract: [
      "document_group",
      "group_title",
      "parent_tags",
      "document_sub_group",
      "sub_group_title",
      "group_tags",
    ],
  },
  section_header: {
    component: LazyImport(() => import("@/blades/SectionHeaderBlade")),
    extract: ["header", "sub_header"],
  },
  carousel: {
    component: LazyImport(() => import("@/blades/carouselBlade2/CarouselBlade2")),
    extract: [],
  },
  image_text_button_blade: {
    component: LazyImport(() => import("@/blades/ImageKickerBlade")),
    extract: ["image", "title"],
  },
  component_placeholder: {
    component: LazyImport(() => import("@/blades/PlaceHolderBlade")),
    extract: [
      'target'
    ],
  },
  placeholder: {
    component: LazyImport(() => import("@/blades/PlaceHolderBlade")),
    extract: [
        'target'
    ],
  },
  quick_links_item: {
    component: LazyImport(() => import("@/blades/QuickLinksItemBlade")),
    extract: [
      'quick_link_title'
    ],
  },
  anchor: {
    component: LazyImport(() => import("@/blades/AnchorBlade")),
    extract: [
      'anchor'
    ],
  },
  quad_blade_three_one: {
    component: withDebug(UnimplementedComponent),
    extract: [],
  },
  gallery_section_header: {
    component: withDebug(UnimplementedComponent),
    extract: [],
  },
  gallery_header: {
    component: withDebug(UnimplementedComponent),
    extract: [],
  },
  gallery_placeholder: {
    component: withDebug(UnimplementedComponent),
    extract: [],
  },
  blog_detail: {
    component: LazyImport(() => import("@/blades/BlogDetailBlade")),
    extract: ["author", "blog_image"],
  },
  cinema_pane: {
    component: LazyImport(() => import("@/blades/CinemaPaneBlade")),
    extract: ["entity", "is_full_bleed"],
  },
  cinema_pane_two: {
    component: LazyImport(() => import("@/blades/CinemaPaneTwoBlade")),
    extract: ["entity", "is_full_bleed", "text_theme"],
  },
  cta_buttons: {
    component: LazyImport(() => import("@/blades/CtaButtonBlade")),
    extract: ["entity"],
  },
  product_slider: {
    component: withDebug(UnimplementedComponent),
    extract: [
      "tagline",
      "url",
      "product_slider_group",
      "product_slide",
      "name",
      "image",
    ],
  },
  triptych: {
    component: LazyImport(() => import("@/blades/Triptych")),
    extract: [
      "kicker",
      "call_to_action",
      "product_info",
      "image_one",
      "image_two",
      "image_three",
    ],
  },
  image_plus_blade: {
    component: LazyImport(() => import("@/blades/ImagePlusBlade")),
    extract: ["image", "caption", "image_mode"],
  },
  blade_quote: {
    component: LazyImport(() => import("@/blades/QuoteBlade")),
    extract: ["quote", "quoted", "quoted_detail"],
  },
  latest_post_hero: {
    component: LazyImport(() => import("@/blades/LatestPostHeroBlade")),
    extract: [
      "content_type_select",
      "content_tags",
      "text_theme"
    ],
  },
  content_directory: {
    component: LazyImport(() => import("@/blades/ContentDirectoryBlade")),
    extract: [
      "button_text",
      "content_type_select",
      "title",
      "search",
      "content_tags",
      "show_date",
      "exclude_first"
    ],
  },
  full_bleed_video: {
    component: LazyImport(() => import("@/blades/FullBleedVideo")),
    extract: ["video_url"],
  },
  full_bleed_carousel: {
    component: LazyImport(() => import("@/blades/fullBleedCarousel/FullBleedCarousel")),
    extract: ["text_theme", "align_copy_horizontal", "align_copy_vertical", "buttons", "button"],
  },
};
